import {
  useActivatePromoBanner,
  useDeactivatePromoBanner,
  useDeletePromoBanner,
} from '@/api/promo-banner/promo-banner.hooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { getS3Url } from '@/config/s3.config';
import { PromoBannerResponse } from '@repo/types/src/promo-banner';
import dayjs from 'dayjs';
import { Trash } from 'lucide-react';
import { toast } from 'sonner';
import { AddPromoBanner } from './AddPromoBanner';

export const PromoBannerCard = (banner: PromoBannerResponse) => {
  const activateMutation = useActivatePromoBanner();
  const deactivateMutation = useDeactivatePromoBanner();
  const deleteMutation = useDeletePromoBanner();

  const handleActivate = () => {
    activateMutation.mutate(banner.id, {
      onSuccess: () => toast.success('Bannière activée !', { duration: 3000 }),
      onError: () =>
        toast.error("Erreur lors de l'activation", { duration: 3000 }),
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(banner.id, {
      onSuccess: () =>
        toast.success('Bannière supprimée !', { duration: 3000 }),
      onError: () =>
        toast.error('Erreur lors de la suppression', { duration: 3000 }),
    });
  };

  return (
    <Card className="overflow-hidden w-72 h-fit">
      <CardContent className="p-0 aspect-[4/3] w-full relative">
        <img
          src={getS3Url(banner.image)}
          alt="Promo banner"
          className="object-cover w-full h-full"
        />
        <div className="absolute top-2 left-2 flex gap-1">
          <Badge variant={banner.region === 'US' ? 'default' : 'secondary'}>
            {banner.region === 'US' ? '🇺🇸 Orpair' : '🇫🇷 MC'}
          </Badge>
          {banner.active && <Badge className="bg-green-600">Active</Badge>}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-start gap-2 pt-3 pb-3">
        <p className="text-sm text-zinc-700 leading-snug">
          {banner.textParts.map((part, i) =>
            part.highlight ? (
              <strong key={i}>
                {part.text}
                {i < banner.textParts.length - 1 ? ' ' : ''}
              </strong>
            ) : (
              <span key={i}>
                {part.text}
                {i < banner.textParts.length - 1 ? ' ' : ''}
              </span>
            ),
          )}
        </p>
        <p className="text-xs text-zinc-400">
          Bouton : {banner.buttonText} · Ajoutée le{' '}
          {dayjs(banner.createdAt).format('DD MMM YYYY')}
        </p>
        <div className="flex gap-2 w-full justify-end">
          {banner.active ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                deactivateMutation.mutate(banner.id, {
                  onSuccess: () =>
                    toast.success('Bannière désactivée !', { duration: 3000 }),
                  onError: () =>
                    toast.error('Erreur lors de la désactivation', {
                      duration: 3000,
                    }),
                })
              }
            >
              Désactiver
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={handleActivate}>
              Activer
            </Button>
          )}
          <AddPromoBanner banner={banner} />
          <Button size="icon" variant="ghost" onClick={handleDelete}>
            <Trash size={16} color="red" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};
